import type { RefundRequestDetail } from "@/@types/RefundRequest";
import { usePermissions } from "@/auth/PermissionsContext";
import BadgeShared from "@/components/shared/Badge/Badge";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import useModalStore from "@/store/useModalStore";
import { format } from "date-fns";
import { AlertTriangle, Check, ChevronDown, ExternalLink } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRefundRequestDetail, useStartRefundMediation } from "../hooks/useRefundRequests";
import {
  canRespondToRefundRequest,
  canStartRefundMediation,
  refundProgressSteps,
  refundRequestBadgeStatus,
  refundRequestWarrantyState,
  refundSentHintKey,
  responseTimeLeft,
  responseWindowHours,
} from "../refund-request.utils";
import {
  APPROVE_REFUND_REQUEST_MODAL_NAME,
  ApproveRefundRequestModal,
} from "./ApproveRefundRequestModal";
import { InfoTooltip } from "./InfoTooltip";
import {
  RefundRequestConversation,
  RefundRequestMediation,
  RefundRequestTimeline,
} from "./RefundRequestMediation";

const formatDate = (value: string | null | undefined, pattern = "dd/MM/yyyy") =>
  value ? format(new Date(value), pattern) : "—";

function InfoBox({ label, children, wide }: { label: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <div className={cn("rounded-md border p-3 flex flex-col gap-1", wide && "col-span-2")}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium break-words">{children}</span>
    </div>
  );
}

interface RefundRequestDetailSheetProps {
  requestId: string | null;
  onClose: () => void;
}

export function RefundRequestDetailSheet({ requestId, onClose }: RefundRequestDetailSheetProps) {
  const { openModal } = useModalStore();
  const { hasPermission, isPrimary } = usePermissions();
  const { data: request, isLoading } = useRefundRequestDetail(requestId);

  const hasRespondPermission = isPrimary || hasPermission("disputes.respond_disputes");
  const canRespond = canRespondToRefundRequest(request, hasRespondPermission);
  const canMediate = canStartRefundMediation(request, hasRespondPermission);

  return (
    <>
      <CustomSheet open={Boolean(requestId)} onClose={onClose}>
        <div className="p-6 space-y-6">
          {isLoading || !request ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : request.status === "in_mediation" ? (
            <RefundRequestMediation
              request={request}
              canRespond={canRespond}
              onApprove={() => openModal(APPROVE_REFUND_REQUEST_MODAL_NAME)}
              details={<RefundRequestFacts request={request} />}
            />
          ) : (
            <RefundRequestDetailContent
              request={request}
              canRespond={canRespond}
              canMediate={canMediate}
              onApprove={() => openModal(APPROVE_REFUND_REQUEST_MODAL_NAME)}
            />
          )}
        </div>
      </CustomSheet>

      <ApproveRefundRequestModal request={request ?? null} />
    </>
  );
}

interface RefundRequestDetailContentProps {
  request: RefundRequestDetail;
  canRespond: boolean;
  canMediate: boolean;
  onApprove: () => void;
}

function RefundRequestDetailContent({
  request,
  canRespond,
  canMediate,
  onApprove,
}: RefundRequestDetailContentProps) {
  const { t } = useTranslation(["common", "disputes"]);
  const { mutate: startMediation, isPending: isStartingMediation } = useStartRefundMediation();
  const buyerFirstName = request.sale.buyer_name?.trim().split(/\s+/)[0] || t("disputes:detail.the_buyer");
  const hasConversation = request.messages.length > 1;

  const handleStartMediation = () =>
    startMediation(request.id, {
      onError: (error: any) =>
        toast({
          description: error?.response?.data?.message || t("common:data_submission_error"),
          variant: "destructive",
        }),
    });

  return (
    <>
      <header className="flex flex-col gap-2 pr-8">
        <span className="text-xs text-muted-foreground">
          {request.code} · {t("disputes:types.refunds")}
        </span>
        <h2 className="text-2xl font-semibold">{request.amount_formatted}</h2>
        <span className="text-sm text-muted-foreground">
          {request.sale.product ?? "—"} · {request.sale.buyer_name ?? "—"}
        </span>
        <div className="flex items-center gap-3">
          <BadgeShared
            status={refundRequestBadgeStatus(request.status)}
            text={t(`disputes:status.${request.status}`)}
          />
          {request.sale.id && (
            <Link
              to={`/sales/sales-details/${request.sale.id}`}
              className="text-xs text-interactive inline-flex items-center gap-1 hover:underline"
            >
              {t("disputes:detail.view_sale")}
              <ExternalLink size={12} />
            </Link>
          )}
        </div>
      </header>

      <RefundRequestFacts request={request} />

      <RefundRequestTimeline request={request} />

      {request.status === "pending" && <ResponseDeadline request={request} />}

      {canRespond && (
        <section className="space-y-2">
          <div className="flex gap-2">
            <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={onApprove}>
              {t("disputes:detail.approve")}
            </Button>
            {canMediate && (
              <Button
                variant="secondary"
                className="flex-1"
                onClick={handleStartMediation}
                disabled={isStartingMediation}
              >
                {isStartingMediation ? t("common:saving") : t("disputes:detail.mediate")}
              </Button>
            )}
          </div>
          {canMediate && (
            <p className="text-xs text-muted-foreground">
              {t("disputes:detail.mediate_hint", { name: buyerFirstName })}
            </p>
          )}
        </section>
      )}

      {hasConversation && (
        <Collapsible className="rounded-md border">
          <CollapsibleTrigger className="group flex w-full items-center justify-between p-3 text-sm font-medium">
            {t("disputes:mediation.view_conversation")}
            <ChevronDown size={16} className="transition-transform group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t p-3">
            <RefundRequestConversation request={request} />
          </CollapsibleContent>
        </Collapsible>
      )}

      {request.refund_progress ? (
        <RefundProgress request={request} />
      ) : (
        (request.status === "canceled" || request.status === "buyer_withdrew") && (
          <section className="rounded-md border p-4 space-y-2 text-sm">
            <BadgeShared
              status={refundRequestBadgeStatus(request.status)}
              text={t(`disputes:status.${request.status}`)}
            />
            <p className="text-muted-foreground">{t(`disputes:result.${request.status}`)}</p>
          </section>
        )
      )}
    </>
  );
}

function RefundRequestFacts({ request }: { request: RefundRequestDetail }) {
  const { t } = useTranslation(["common", "disputes"]);
  const warrantyState = refundRequestWarrantyState(request);
  const method = request.payment_method
    ? t(`common:${request.payment_method}`, { defaultValue: request.payment_method })
    : "—";

  const warrantyText =
    warrantyState === "closed"
      ? t("disputes:detail.warranty_closed")
      : warrantyState === "expired"
        ? t("disputes:warranty_expired")
        : t("disputes:detail.warranty_until", {
          days: t("disputes:days_left", { count: request.warranty_days_left ?? 0 }),
          date: formatDate(request.warranty_expires_at),
        });

  return (
    <>
      <section className="grid grid-cols-2 gap-3">
        <InfoBox label={t("disputes:detail.reason")}>{request.reason ?? "—"}</InfoBox>
        <InfoBox
          label={
            <span className="inline-flex items-center gap-1">
              {t("disputes:detail.warranty")} <InfoTooltip text={t("disputes:warranty_tooltip")} />
            </span>
          }
        >
          <span className={cn(warrantyState === "ending" && "text-red-500")}>{warrantyText}</span>
        </InfoBox>
        <InfoBox label={t("disputes:detail.payment")}>
          {method}
          {request.paid_at && (
            <span className="block text-xs text-muted-foreground font-normal">
              {formatDate(request.paid_at)}
            </span>
          )}
        </InfoBox>
        <InfoBox label={t("disputes:detail.product_usage")}>
          {request.product_usage.length === 0
            ? t("disputes:detail.no_product_usage")
            : request.product_usage.map((usage, index) => (
                <span key={index} className="block">
                  {usage.access_count > 0
                    ? t("disputes:detail.product_accesses", {
                        product: usage.product ?? "—",
                        count: usage.access_count,
                        date: formatDate(usage.last_access_at),
                      })
                    : t("disputes:detail.access_granted_at", {
                        product: usage.product ?? "—",
                        date: formatDate(usage.granted_at),
                      })}
                </span>
              ))}
          <span className="block text-xs text-muted-foreground font-normal">
            {t("disputes:detail.product_usage_hint")}
          </span>
        </InfoBox>
        <InfoBox label={t("disputes:detail.buyer_email")} wide>
          {request.sale.buyer_email ?? "—"}
        </InfoBox>
      </section>

      {request.items.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">{t("disputes:detail.items")}</h3>
          <ul className="rounded-md border divide-y">
            {request.items.map((item, index) => (
              <li key={index} className="flex items-center justify-between gap-3 p-3 text-sm">
                <span className="truncate">{item.name ?? "—"}</span>
                <span className="font-medium whitespace-nowrap">{item.amount_formatted}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {request.customer_message && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">{t("disputes:detail.customer_message")}</h3>
          <blockquote className="rounded-md border-l-2 border-muted-foreground/40 bg-muted/30 p-3 text-sm italic whitespace-pre-line">
            "{request.customer_message}"
          </blockquote>
        </section>
      )}
    </>
  );
}

function ResponseDeadline({ request }: { request: RefundRequestDetail }) {
  const { t } = useTranslation("disputes");
  const left = responseTimeLeft(request.response_due_at);

  if (!request.response_due_at || !left) return null;

  const leftText =
    left.unit === "overdue"
      ? t("detail.time_left_overdue")
      : t(`detail.time_left_${left.unit}`, { count: left.value });

  return (
    <div className="flex items-start gap-2 rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-200">
      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      <span className="flex-1">
        <strong>{t("detail.your_turn_label")}</strong>{" "}
        {t("detail.your_turn", {
          date: formatDate(request.response_due_at, "dd/MM HH:mm"),
          left: leftText,
        })}
      </span>
      <InfoTooltip
        text={t("detail.step_tooltip", { hours: responseWindowHours(request) ?? 24 })}
      />
    </div>
  );
}

function RefundProgress({ request }: { request: RefundRequestDetail }) {
  const { t } = useTranslation("disputes");
  const progress = request.refund_progress;

  if (!progress) return null;

  const steps = refundProgressSteps(progress);
  const head =
    request.status === "not_done" ? "head_not_done" : request.status === "refunded" ? "head_refunded" : "head_processing";

  const stepText = (key: string) => {
    switch (key) {
      case "approved_seller":
        return progress.approved_by_name
          ? t("progress.approved_seller", { name: progress.approved_by_name })
          : t("progress.approved_seller_anonymous");
      case "sent":
        return t("progress.sent");
      default:
        return t(`progress.${key}`);
    }
  };

  const stepHint = (key: string) => {
    if (key.startsWith("approved_")) {
      return request.status === "not_done"
        ? t("progress.approved_hint_not_done")
        : t("progress.approved_hint", { amount: request.amount_formatted });
    }
    if (key === "sent") return t(`progress.${refundSentHintKey(request.payment_method)}`);
    return t(`progress.${key}_hint`);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium">{t("progress.title")}</h3>
        <BadgeShared status={refundRequestBadgeStatus(request.status)} text={t(`progress.${head}`)} />
      </div>
      <ol className="space-y-3">
        {steps.map((step) => (
          <li key={step.key} className="flex gap-3 text-sm">
            <span
              className={cn(
                "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold",
                step.state === "done" && "border-green-500 bg-green-500/20 text-green-400",
                step.state === "warn" && "border-yellow-500 bg-yellow-500/20 text-yellow-300",
                step.state === "current" && "border-muted-foreground",
              )}
            >
              {step.state === "done" ? <Check size={12} /> : step.state === "warn" ? "!" : null}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">{stepText(step.key)}</span>
              <span className="text-xs text-muted-foreground">{stepHint(step.key)}</span>
              {step.at && (
                <span className="text-xs text-muted-foreground">
                  {formatDate(step.at, "dd/MM/yyyy HH:mm")}
                </span>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
